import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, Edit, Loader2, Palette } from "lucide-react";

function AdminBackgrounds() {
  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBg, setEditingBg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", category: "indoor", preview_image_url: "" });
  const categories = ["indoor", "outdoor", "royal", "nature", "cosmetic"];

  useEffect(() => { fetchBackgrounds(); }, []);

  const fetchBackgrounds = async () => {
    try {
      const response = await axios.get(API + "/backgrounds", { withCredentials: true });
      setBackgrounds(response.data);
    } catch (error) {
      toast.error("Failed to load backgrounds");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => { setFormData({ name: "", category: "indoor", preview_image_url: "" }); setEditingBg(null); };
  
  const handleEdit = (bg) => {
    setEditingBg(bg);
    setFormData({ name: bg.name, category: bg.category, preview_image_url: bg.preview_image_url });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.preview_image_url) { toast.error("Please fill in all required fields"); return; }
    setSaving(true);
    try {
      if (editingBg) {
        await axios.put(API + "/admin/backgrounds/" + editingBg.background_id, formData, { withCredentials: true });
        toast.success("Background updated successfully");
      } else {
        await axios.post(API + "/admin/backgrounds", formData, { withCredentials: true });
        toast.success("Background created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchBackgrounds();
    } catch (error) {
      toast.error("Failed to save background");
    } finally {
      setSaving(false);
    }
  };

  const getBackgroundsByCategory = (cat) => backgrounds.filter(bg => bg.category === cat);

  if (loading) {
    return React.createElement(DashboardLayout, null,
      React.createElement("div", { className: "flex items-center justify-center h-96" },
        React.createElement("div", { className: "w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" })
      )
    );
  }

  const renderCategorySection = (category) => {
    const bgs = getBackgroundsByCategory(category);
    if (bgs.length === 0) return null;
    return React.createElement("div", { key: category },
      React.createElement("h2", { className: "font-display text-lg font-semibold text-neutral-900 mb-4 capitalize flex items-center gap-2" },
        React.createElement(Palette, { className: "w-5 h-5 text-indigo-600" }), category
      ),
      React.createElement("div", { className: "grid md:grid-cols-4 lg:grid-cols-5 gap-4" },
        bgs.map(bg => React.createElement(Card, { key: bg.background_id, className: "border-neutral-100 overflow-hidden group cursor-pointer", onClick: () => handleEdit(bg) },
          React.createElement("div", { className: "relative" },
            React.createElement("img", { src: bg.preview_image_url, alt: bg.name, className: "w-full aspect-video object-cover" }),
            React.createElement("div", { className: "absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" },
              React.createElement(Button, { size: "sm", className: "bg-white text-neutral-900 hover:bg-neutral-100" },
                React.createElement(Edit, { className: "w-4 h-4 mr-2" }), "Edit"
              )
            )
          ),
          React.createElement(CardContent, { className: "p-3" },
            React.createElement("p", { className: "font-medium text-neutral-900 text-sm" }, bg.name)
          )
        ))
      )
    );
  };

  return React.createElement(DashboardLayout, null,
    React.createElement("div", { "data-testid": "admin-backgrounds-page", className: "space-y-6" },
      React.createElement("div", { className: "flex items-center justify-between" },
        React.createElement("div", null,
          React.createElement("h1", { className: "font-display text-2xl md:text-3xl font-bold text-neutral-900" }, "Backgrounds"),
          React.createElement("p", { className: "text-neutral-600 mt-1" }, "Manage background options")
        ),
        React.createElement(Dialog, { open: dialogOpen, onOpenChange: (open) => { setDialogOpen(open); if (!open) resetForm(); } },
          React.createElement(DialogTrigger, { asChild: true },
            React.createElement(Button, { className: "bg-indigo-700 hover:bg-indigo-800 rounded-full" },
              React.createElement(Plus, { className: "w-4 h-4 mr-2" }), "Add Background"
            )
          ),
          React.createElement(DialogContent, { className: "max-w-md" },
            React.createElement(DialogHeader, null,
              React.createElement(DialogTitle, null, editingBg ? "Edit Background" : "Add New Background")
            ),
            React.createElement("div", { className: "space-y-4 mt-4" },
              React.createElement("div", null,
                React.createElement(Label, null, "Name"),
                React.createElement(Input, { value: formData.name, onChange: (e) => setFormData(prev => ({ ...prev, name: e.target.value })), placeholder: "Background name", className: "mt-1" })
              ),
              React.createElement("div", null,
                React.createElement(Label, null, "Category"),
                React.createElement("select", { className: "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg", value: formData.category, onChange: (e) => setFormData(prev => ({ ...prev, category: e.target.value })) },
                  categories.map(cat => React.createElement("option", { key: cat, value: cat }, cat.charAt(0).toUpperCase() + cat.slice(1)))
                )
              ),
              React.createElement("div", null,
                React.createElement(Label, null, "Preview Image URL"),
                React.createElement(Input, { value: formData.preview_image_url, onChange: (e) => setFormData(prev => ({ ...prev, preview_image_url: e.target.value })), placeholder: "https://...", className: "mt-1" })
              ),
              formData.preview_image_url && React.createElement("img", { src: formData.preview_image_url, alt: "Preview", className: "w-full aspect-video object-cover rounded-lg" }),
              React.createElement(Button, { onClick: handleSave, disabled: saving, className: "w-full bg-indigo-600 hover:bg-indigo-700" },
                saving && React.createElement(Loader2, { className: "w-4 h-4 animate-spin mr-2" }),
                editingBg ? "Update Background" : "Create Background"
              )
            )
          )
        )
      ),
      renderCategorySection("indoor"),
      renderCategorySection("outdoor"),
      renderCategorySection("royal"),
      renderCategorySection("nature"),
      renderCategorySection("cosmetic")
    )
  );
}

export default AdminBackgrounds;
